const organization = {
  usage: {
    title: 'Team usage',
    subtitle:
      'Review recruiting output, credit consumption, and recorded business activity over time.',
    period: 'Reporting period',
    periods: { today: 'Today', '7d': 'Last 7 days', '30d': 'Last 30 days' },
    metrics: {
      activeMembers: 'Active contributors',
      newJobs: 'New jobs',
      newTags: 'New tags',
      imports: 'Imported candidates',
      aiConsumed: 'AI credits consumed',
      contactConsumed: 'Contact credits consumed',
      jobs: 'Organization jobs',
      tags: 'Organization tags',
      candidates: 'Organization candidates',
    },
    tabs: {
      employees: 'Member usage',
      activity: 'Business activity',
      points: 'Credit ledger',
      jobs: 'Employee jobs',
      tags: 'Employee tags',
    },
    activeHint:
      'Active contributors created an organization job or tag, or consumed organization credits during this period. This is not a login count. Consumption totals actual charges, excluding allocations and reclaims. Refunds appear separately and do not reduce this total.',
    partialHistory:
      'Only records with confirmed organization ownership are included. Job and tag counts use surviving records; deleted or uncollected history cannot be fully reconstructed. Personal records from before joining are excluded.',
    missingSources:
      'Candidate imports, AI searches, and member management actions are not fully collected and are excluded from business activity statistics.',
    notCollected: 'Not collected',
    range: '{start} to {end} (UTC)',
    timeUtc: 'Time (UTC)',
    lastActive: 'Latest activity (UTC)',
    details: 'Details',
    inspectEmployee: "View {name}'s organization activity",
    employeeDetail: 'Employee business details',
    employeeMetricHint:
      'Jobs, tags, and candidates are current organization totals. Credit consumption covers {period} only. The job and tag lists below include all dates.',
    memberUnavailable: 'This member record is no longer available. Refresh the member list.',
    activityHint:
      'Recorded job and tag creation and credit changes. This is not a complete audit trail.',
    ledgerHint:
      'Actual charges, refunds, quota allocations, and organization pool adjustments. Charges and refunds are recorded separately; the overview shows gross charges, not net consumption after refunds. The action determines what the credit amount represents.',
    retry: 'Retry',
    noRecords: 'No records in this period',
    event: 'Action',
    points: 'Credits',
    poolBalance: 'Organization pool after',
    operator: 'Operator',
    resource: 'Related record',
    organizationPool: 'Organization pool',
    events: {
      job_created: 'Created a job',
      tag_created: 'Created a tag',
      points_changed: 'Credit change',
    },
    actions: {
      grant: 'Initial grant',
      renew: 'Renewal grant',
      allocate: 'Allocate quota',
      reclaim: 'Reclaim quota',
      consume: 'Credit charge',
      refund: 'Credit refund',
      freeze: 'Freeze credits',
      unfreeze: 'Unfreeze credits',
      adjust: 'Adjust credits',
    },
  },
  page: {
    tabs: { members: 'Members', resources: 'Team resources', usage: 'Usage' },
    memberCount: '{count} members',
    employeeResources: 'Resources',
    moreActions: 'More member actions',
    membersHelp: 'Seat and resource scope',
    statisticsHelp: 'Metric definitions and coverage',
    aiPool: 'AI credits available to allocate',
    contactPool: 'Contact credits available to allocate',
    eyebrow: 'Organization',
    benefitsTitle: 'Seats and credits',
    memberBenefitsTitle: 'My organization benefits',
  },
  data: {
    title: 'Team recruiting overview',
    subtitle: 'Review employee tags, jobs, and candidate activity within your organization.',
    activeMembers: 'Active members',
    employees: 'Employees',
    employee: 'Employee',
    allEmployees: 'All employees',
    tags: 'Tags',
    jobs: 'Jobs',
    candidates: 'Candidates',
    view: 'View',
    viewCandidates: 'View candidates',
    search: 'Search',
    searchEmployees: 'Search employee name or email',
    searchResources: 'Search by name',
    tagName: 'Tag name',
    jobName: 'Job name',
    createdAt: 'Created',
    defaultTag: 'Default talent pool',
    viewTags: "View {name}'s tags",
    viewJobs: "View {name}'s jobs",
    empty: 'No matching organization records',
    scope:
      'Only records attributed to this organization are shown. Personal records from before joining are excluded.',
    deduplication:
      'Candidate counts deduplicate tag and job bindings. Summing employee counts may exceed the organization total.',
    forbidden:
      'Organization administrator access is required. Refresh your organization information.',
    unavailable: 'Organization reporting is not ready. Please contact an administrator.',
    loadFailed: 'Unable to load organization data. Use Refresh to try again.',
    left: 'Left',
    removed: 'Removed',
    inactive: 'Inactive',
    closed: 'Closed',
    pendingJob: 'Pending analysis',
    analyzingJob: 'Analyzing',
    readyJob: 'Analyzed',
    failedJob: 'Analysis failed',
    previous: 'Previous',
    next: 'Next',
    pagination: '{total} records · Page {page} of {pages}',
  },
  ownerAiPoolDesc: 'Organization AI credits available to the owner',
  ownerContactPoolDesc: 'Organization contact credits available to the owner',
  ownerUsesPool: 'Uses the organization pool',
  ownerPoolHint:
    'The owner uses the remaining organization pool directly. Credits allocated to members are reserved for those members.',
  title: 'Organization & Seats',
  adminTitle: 'Organization Management',
  inviteNotice: 'You have been invited to join {name} enterprise seat',
  inviteAccepted: 'Joined organization successfully',
  inviteRejected: 'Invitation declined',
  memberContactQuotaDesc: 'Allocated enterprise contact credits',
  memberAiQuotaDesc: 'Allocated enterprise AI analysis points',
  accept: 'Accept',
  reject: 'Decline',
  enterpriseBadge: 'Enterprise',
  myOrganization: 'My Organization',
  quotaAllocation: 'Quota Allocation',
  members: 'Members',
  ledger: 'Ledger',
  empty: 'No data',
  refresh: 'Refresh',
  loading: 'Loading...',
  submit: 'Submit',
  confirm: 'Confirm',
  cancel: 'Cancel',
  status: 'Status',
  actions: 'Actions',
  time: 'Time',
  role: 'Role',
  email: 'Email',
  name: 'Name',
  active: 'Active',
  expired: 'Expired',
  pending: 'Pending',
  admin: 'Enterprise Admin',
  member: 'Employee',
  enterpriseAdmin: 'Enterprise Admin',
  enterpriseMember: 'Employee',

  // Platform Admin
  adminPageTitle: 'Organization & Subscription Management',
  openEnterprisePackage: 'Open Enterprise Package',
  searchOrgPlaceholder: 'Search by enterprise name...',
  noOrganizations: 'No organizations found',
  colOrgNameId: 'Company Name / Logto ID',
  colAdminUser: 'Enterprise Admin',
  colSeatCapacity: 'Seat Capacity (Active/Pending/Total)',
  colAiPool: 'Enterprise AI Points Pool',
  colContactPool: 'Enterprise Contact Points Pool',
  colValidity: 'Validity Period',
  colStatus: 'Status',
  remainAvailable: '{count} available',
  totalCount: 'Total {count}',
  startAt: 'From',
  endAt: 'To',
  renewOrAdd: 'Renew / Add',
  transferAdmin: 'Transfer Admin',
  editOrg: 'Edit',
  deleteOrg: 'Delete',
  editModalTitle: 'Edit Organization',
  editModalDesc: 'Update enterprise name and active status.',
  statusDisabled: 'Disabled',
  statusDeleted: 'Deleted',
  deleteModalTitle: 'Delete Organization',
  deleteModalMsg:
    'Are you sure you want to delete "{name}"? All members will immediately lose enterprise entitlements, unconsumed quotas will be reclaimed, and the subscription terminated. This action cannot be undone.',
  confirmDelete: 'Confirm Delete',
  deleteSuccess: 'Organization deleted successfully',
  updateSuccess: 'Organization updated successfully',
  openModalTitle: 'Open Enterprise Package',
  renewModalTitle: 'Renew / Expand Enterprise Package',
  transferModalTitle: 'Transfer Enterprise Admin',
  transferModalDesc:
    'Transfer administrator of "{name}" to a new employee. The new admin must have a registered account.',
  logtoOrgId: 'Logto Org ID',
  logtoOrgIdPlaceholder: 'e.g. org_xxxxxx',
  companyName: 'Company Name',
  companyNamePlaceholder: 'Enter company name',
  adminEmail: 'Primary Admin Email',
  adminEmailPlaceholder: 'Registered account email of admin',
  newAdminEmail: 'New Admin Email',
  newAdminEmailPlaceholder: 'Registered email of new admin',
  staffSeatCount: 'Employee Seats N (Total 1+N)',
  actualTotalSeats: 'Total Seats',
  seatsUnit: 'seats',
  startDate: 'Start Date',
  endDate: 'End Date',
  grantAiPoints: 'Enterprise Total AI Points',
  addAiPoints: 'Additional AI Points',
  grantContactPoints: 'Enterprise Total Contact Points',
  addContactPoints: 'Additional Contact Points',
  submitting: 'Submitting...',
  processing: 'Processing...',
  confirmOpen: 'Confirm Open',
  confirmTransfer: 'Confirm Transfer',
  openSuccess: 'Enterprise package opened successfully',
  renewSuccess: 'Renewed successfully',
  transferSuccess: 'Admin transferred successfully',
  fillCompleteInfo: 'Please complete all enterprise details',

  // Enterprise User View
  defaultOrgTitle: 'Organization & Entitlements Management',
  noOrgAccess: 'You have not joined any organization and do not have access to this page',
  expiredFrozenBadge: 'Subscription Expired & Frozen',
  orgSubtitle:
    'Manage 1+N seats, invitations, enterprise AI points pool, and contact credits distribution',
  inviteEmployee: 'Invite Employee',
  leaveOrg: 'Leave Organization',
  expiredBannerTitle: 'Enterprise package expired, enterprise privileges are frozen',
  expiredBannerDesc:
    'Unused points remain frozen. Quota allocations and charges are stopped, but viewing members, cancelling invitations, removing members, and voluntary leaving are preserved. Please contact platform admin to renew.',
  totalSeatCapacity: 'Total Seat Capacity',
  seatCountUnit: 'seats',
  activatedSeats: 'Active',
  invitedSeats: 'Pending',
  remainSeats: 'Available',
  aiPoolAvailable: 'Remaining Allocatable AI Points Pool',
  contactPoolAvailable: 'Remaining Allocatable Contact Points Pool',
  totalPurchased: 'Total Purchased',
  packageValidUntil: 'Package Valid Until',
  normalStatus: 'Active',
  myAvailableAiQuota: 'My Available Enterprise AI Points',
  myAvailableContactQuota: 'My Available Enterprise Contact Points',
  consumedQuota: 'Consumed',
  membersTableTitle: 'Enterprise Members & Seats',
  membersTableSubtitle:
    'Seat occupancy includes active members and invitations pending within 7 days',
  colMemberEmail: 'Member / Email',
  colSeatStatus: 'Seat Status',
  colAiQuota: 'AI Points (Available / Consumed)',
  colContactQuota: 'Contact Points (Available / Consumed)',
  daysExpiry: '7-day expiry',
  cancelInvite: 'Cancel Invite',
  allocateQuota: 'Adjust Quota',
  removeMember: 'Remove Member',
  activatedAtLabel: 'Activated',
  invitedAtLabel: 'Invited',
  noMembers: 'No members',
  consumedLabel: 'Consumed',

  // Invite Modal
  inviteModalTitle: 'Invite Employee to Organization',
  inviteModalDesc:
    'Enter the registered email of the employee on EasyHire AI. 1 seat will be reserved for 7 days.',
  employeeEmailLabel: 'Employee Registered Email',
  employeeEmailPlaceholder: 'Employee email registered on platform',
  sendInvite: 'Send Invite',
  sending: 'Sending...',
  inviteSuccess: 'Invitation sent successfully',
  pleaseInputEmail: 'Please enter employee email',

  // Quota Modal
  quotaModalTitle: 'Allocate Member Quotas',
  quotaModalDesc:
    'Adjust available AI match points and contact credits for members. Increases will allocate from the enterprise pool, while decreases reclaim unused credits back to pool.',
  targetMemberLabel: 'Target Member',
  aiPointsTitle: 'AI Match Points',
  contactPointsTitle: 'Contact Credits',
  poolRemainingLabel: 'Pool Remaining',
  currentAvailableQuota: 'Current Available',
  consumedQuotaLabel: 'Consumed',
  targetQuotaLabel: 'Target Quota',
  quickPresetLabel: 'Quick Adjustment',
  presetCustom: 'Custom Input',
  presetAdd100: 'Add +100 pts',
  presetAdd500: 'Add +500 pts',
  presetAdd1000: 'Add +1000 pts',
  presetAdd10: 'Add +10 pts',
  presetAdd50: 'Add +50 pts',
  presetClear: 'Reclaim all available quota',
  diffIncrease: 'Allocate +{count} from pool',
  diffDecrease: 'Reclaim {count} to pool',
  diffUnchanged: 'No change',
  exceedPoolWarning: 'Allocation exceeds available pool balance!',
  pointsTypeLabel: 'Points Type',
  aiPointsOption: 'AI Points (Pool remaining: {pool})',
  contactPointsOption: 'Contact Points (Pool remaining: {pool})',
  confirmAdjust: 'Confirm Allocation',
  adjustSuccess: 'Quota updated successfully',

  // Dialog Confirmations
  cancelInviteConfirmTitle: 'Cancel Invitation',
  cancelInviteConfirmMsg:
    'Are you sure you want to cancel this invitation? The reserved seat will be released immediately.',
  cancelInviteSuccess: 'Invitation cancelled, seat released',
  removeMemberConfirmTitle: 'Remove Member',
  removeMemberConfirmMsg:
    'Are you sure you want to remove {name}? Unconsumed quota will be returned to the enterprise pool and the seat will be released.',
  removeMemberSuccess: 'Member removed',
  leaveOrgConfirmTitle: 'Leave Enterprise',
  leaveOrgConfirmMsg:
    'Are you sure you want to leave the enterprise? Unconsumed quota will return to the pool, and your account will return to individual mode.',
  // Platform Points Adjustment
  adjustPoints: 'Adjust Points',
  adjustPointsModalTitle: 'Adjust Enterprise Points Pool',
  adjustPointsModalDesc:
    'Manually add, deduct, or set points for the enterprise pool. Changes will be recorded in audit ledger.',
  currentAiPool: 'Current AI Points Pool',
  currentContactPool: 'Current Contact Credits Pool',
  adjustTypeLabel: 'Points Type',
  adjustModeLabel: 'Adjustment Mode',
  adjustModeAdd: 'Add (+)',
  adjustModeDeduct: 'Deduct (-)',
  adjustModeSet: 'Set Directly (=)',
  adjustAmountLabel: 'Amount',
  adjustAmountPlaceholder: 'Enter adjustment amount',
  adjustReasonLabel: 'Reason / Notes',
  adjustReasonPlaceholder: 'Enter reason (e.g. refund deduction, promotion grant, optional)',
  previewPoolLabel: 'Expected Balance After Adjustment',
  exceedDeductWarning: 'Deduction amount cannot exceed current available pool balance!',
  adjustPointsSuccess: 'Enterprise points pool adjusted successfully',
}

export default organization
